/**
 * The particle field behind the home page.
 *
 * One cloud of points that re-forms into a different shape for each section as
 * the page scrolls: `</>` → the selected service → a knot → stacked work cards
 * → a globe → the Voilacode mark. Between shapes the points pass through a
 * scattered state, so a transition reads as dissolve-and-reassemble rather
 * than a straight tween.
 *
 * Plain three.js, no React: the caller hands over a canvas and gets back a
 * handle. Everything animates in one requestAnimationFrame loop that writes
 * directly into the position buffer, so React never re-renders for motion.
 */
import * as THREE from 'three';

type V2 = [number, number];
type V3 = [number, number, number];

export type ParticleField = {
  /** Morph the services shape to the one at this index. */
  setService: (index: number) => void;
  dispose: () => void;
};

type Options = {
  canvas: HTMLCanvasElement;
  /** Element whose `[data-shape]` sections drive the morph. */
  root: HTMLElement;
  /** Translated horizontally with scroll. */
  marquee: HTMLElement | null;
  service: number;
};

export const COUNT = 13000;
const STARS = 2600;
/** Index of the services section and of the closing mark in the shape list. */
const SERVICES = 1;
const MARK = 5;
/** Which half of a wide screen the shape sits in, per section. */
const SIDE = [1, -1, 1, -1, 1, -1];

/* [brand, secondary, highlight] — light mode needs darker points to read. */
export const PALETTE = {
  dark: ['#7B45F5', '#A585FF', '#ECEAF2'],
  light: ['#5E19EC', '#9B6BFF', '#2A2540'],
};

export const smooth = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

/** The point shader both fields share: soft round sprites, sized by depth. */
export function createMaterial(pixelRatio: number) {
  return new THREE.ShaderMaterial({
    uniforms: { uPR: { value: pixelRatio }, uOp: { value: 1 }, uLight: { value: 0 } },
    vertexShader:
      'attribute float aSize; attribute vec3 aColor; varying vec3 vC; uniform float uPR; void main(){ vC=aColor; vec4 mv=modelViewMatrix*vec4(position,1.0); gl_PointSize=aSize*uPR*(26.0/-mv.z); gl_Position=projectionMatrix*mv; }',
    fragmentShader:
      'varying vec3 vC; uniform float uOp; uniform float uLight; void main(){ float d=length(gl_PointCoord-0.5); if(d>0.5) discard; float a=smoothstep(0.5,0.0,d); gl_FragColor=vec4(mix(vC*a,vC,uLight),a*uOp); }',
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
}

/** A static star field far behind the shape. */
export function createStars(count: number) {
  const pos = new Float32Array(count * 3);
  const size = new Float32Array(count);
  const col = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    pos[i * 3] = (Math.random() - 0.5) * 50;
    pos[i * 3 + 1] = (Math.random() - 0.5) * 60;
    pos[i * 3 + 2] = -4 - Math.random() * 26;
    size[i] = 0.4 + Math.random() * 1.2;
    const b = 0.25 + Math.random() * 0.4;
    col[i * 3] = b;
    col[i * 3 + 1] = b;
    col[i * 3 + 2] = b * 1.15;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
  geo.setAttribute('aColor', new THREE.BufferAttribute(col, 3));
  return geo;
}

export function buildShapes(N: number) {
  const R = Math.random;
  const P = Math.PI;

  const make = (fn: (i: number) => V3) => {
    const a = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const p = fn(i);
      a[i * 3] = p[0];
      a[i * 3 + 1] = p[1];
      a[i * 3 + 2] = p[2];
    }
    return a;
  };
  /** A random point on the outline of a w×h rectangle. */
  const edge = (w: number, h: number): V2 => {
    const t = R() * 2 * (w + h);
    if (t < w) return [t - w / 2, h / 2];
    if (t < w + h) return [w / 2, h / 2 - (t - w)];
    if (t < 2 * w + h) return [w / 2 - (t - w - h), -h / 2];
    return [-w / 2, -h / 2 + (t - 2 * w - h)];
  };
  const seg = (p: V3, q: V3): V3 => {
    const t = R();
    return [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t, p[2] + (q[2] - p[2]) * t];
  };
  const rx = (p: V3, a: number): V3 => [
    p[0],
    p[1] * Math.cos(a) - p[2] * Math.sin(a),
    p[1] * Math.sin(a) + p[2] * Math.cos(a),
  ];
  const ry = (p: V3, a: number): V3 => [
    p[0] * Math.cos(a) + p[2] * Math.sin(a),
    p[1],
    -p[0] * Math.sin(a) + p[2] * Math.cos(a),
  ];
  const tr = (p: V3, x: number, y: number, z: number): V3 => [p[0] + x, p[1] + y, p[2] + z];
  /** A random point on one of the 12 edges of a box. */
  const boxEdge = (w: number, h: number, d: number): V3 => {
    const e = (R() * 12) | 0;
    const t = R() - 0.5;
    const s1 = e & 1 ? 1 : -1;
    const s2 = e & 2 ? 1 : -1;
    if (e < 4) return [t * w, (s1 * h) / 2, (s2 * d) / 2];
    if (e < 8) return [(s1 * w) / 2, t * h, (s2 * d) / 2];
    return [(s1 * w) / 2, (s2 * h) / 2, t * d];
  };
  /** A random point on one of n indented "lines of code" inside a w×h panel. */
  const lines = (w: number, h: number, n: number): V2 => {
    const r = (R() * n) | 0;
    const ind = (r % 4) * 0.28;
    const x0 = -w / 2 + 0.3 + ind;
    const maxL = w - 0.6 - ind;
    const len = Math.max(0.3, maxL * (0.35 + (((r * 37) % 10) / 10) * 0.6));
    return [x0 + R() * len, h / 2 - 0.3 - r * ((h - 0.6) / (n - 1)) + (R() - 0.5) * 0.04];
  };
  /** Rasterise a drawing and return the filled pixels, to sample points from. */
  const sample = (W: number, H: number, draw: (cx: CanvasRenderingContext2D) => void): V2[] => {
    const cv = document.createElement('canvas');
    cv.width = W;
    cv.height = H;
    const cx = cv.getContext('2d');
    const px: V2[] = [];
    if (cx) {
      cx.fillStyle = '#fff';
      draw(cx);
      const d = cx.getImageData(0, 0, W, H).data;
      for (let y = 0; y < H; y += 2)
        for (let x = 0; x < W; x += 2) if (d[(y * W + x) * 4 + 3] > 128) px.push([x, y]);
    }
    return px.length ? px : [[W / 2, H / 2]];
  };

  // ── section shapes ────────────────────────────────────────────────────────
  const gpx = sample(600, 260, (cx) => {
    cx.textAlign = 'center';
    cx.textBaseline = 'middle';
    cx.font = '700 240px "JetBrains Mono Variable", ui-monospace, monospace';
    cx.fillText('</>', 300, 135);
  });
  const glyph = make(() => {
    const p = gpx[(R() * gpx.length) | 0];
    return [(p[0] / 600 - 0.5) * 6.2, -(p[1] / 260 - 0.5) * 2.7, (R() - 0.5) * 0.7];
  });
  const knot = make(() => {
    const t = R() * P * 2;
    const r = Math.cos(3 * t) + 2.1;
    const u = R() * P * 2;
    const v = Math.acos(2 * R() - 1);
    const rad = 0.22 * Math.cbrt(R());
    return [
      r * Math.cos(2 * t) * 0.78 + rad * Math.sin(v) * Math.cos(u),
      r * Math.sin(2 * t) * 0.78 + rad * Math.sin(v) * Math.sin(u),
      -Math.sin(3 * t) * 0.78 + rad * Math.cos(v),
    ];
  });
  const cards: V3[] = [[-0.9, 0.6, -0.8], [0, 0, 0], [0.9, -0.6, 0.8]];
  const work = make(() => {
    const o = cards[(R() * 3) | 0];
    const w = 2.8;
    const h = 1.8;
    const q = R();
    let p: V2;
    if (q < 0.45) p = edge(w, h);
    else if (q < 0.7) {
      p = edge(w - 0.4, h * 0.45);
      p = [p[0], p[1] + 0.25];
    } else if (q < 0.85) p = [-w / 2 + 0.2 + R() * 1.4, -0.45];
    else p = [-w / 2 + 0.2 + R() * 0.9, -0.65];
    return ry(tr([p[0], p[1], (R() - 0.5) * 0.04], o[0], o[1], o[2]), -0.35);
  });
  const G = 2.2;
  const sph = (lat: number, lon: number, r = G): V3 => [
    r * Math.cos(lat) * Math.sin(lon),
    r * Math.sin(lat),
    r * Math.cos(lat) * Math.cos(lon),
  ];
  const globe = make(() => {
    const k = R();
    if (k < 0.5) return sph(Math.asin(2 * R() - 1), R() * P * 2, G * (0.98 + R() * 0.04));
    if (k < 0.75) return sph(((((R() * 7) | 0) - 3) * 22 * P) / 180, R() * P * 2);
    if (k < 0.95) return sph(Math.asin(2 * R() - 1), (((R() * 12) | 0) * 30 * P) / 180);
    // A denser patch over Hyderabad (17.4°N, 78.4°E).
    return sph(
      ((17.4 + (R() - 0.5) * 6) * P) / 180,
      ((78.4 + (R() - 0.5) * 6) * P) / 180 - P / 2.4,
      G * 1.02,
    );
  });
  /** 1 for the points that orbit the mark rather than sit in it. */
  const orbit = new Int8Array(N);
  const mark = make((i) => {
    const q = R();
    if (q < 0.3) {
      const a = R() * P * 2;
      const r = 2.15 + (R() - 0.5) * 0.16;
      return [Math.cos(a) * r, Math.sin(a) * r, (R() - 0.5) * 0.25];
    }
    if (q < 0.4) {
      const a = R() * P * 2;
      const r = 2.15 * Math.sqrt(R());
      return [Math.cos(a) * r, Math.sin(a) * r, (R() - 0.5) * 0.1 - 0.2];
    }
    if (q < 0.8) {
      const p = gpx[(R() * gpx.length) | 0];
      return [(p[0] / 600 - 0.5) * 3.3, -(p[1] / 260 - 0.5) * 1.45, (R() - 0.5) * 0.35];
    }
    orbit[i] = 1;
    const a = R() * P * 2;
    const r = 3.0 + (R() - 0.5) * 0.35;
    return [Math.cos(a) * r, Math.sin(a) * r * 0.32, Math.sin(a) * r * 0.1 + (R() - 0.5) * 0.08];
  });

  // ── one shape per service, in the order the page lists them ───────────────
  const LW = 4;
  const LH = 2.5;
  const LD = 2.6;
  const laptop = make(() => {
    const q = R();
    let p: V3;
    if (q < 0.18) {
      const e = edge(LW, LH);
      p = rx([e[0], e[1] + LH / 2, 0], -0.22);
    } else if (q < 0.45) {
      const l = lines(LW, LH, 9);
      p = rx([l[0], l[1] + LH / 2, 0], -0.22);
    } else if (q < 0.6) {
      const e = edge(LW + 0.2, LD);
      p = [e[0], 0, e[1] + LD / 2];
    } else if (q < 0.88) {
      const c = (R() * 13) | 0;
      const r = (R() * 5) | 0;
      p = [-LW / 2 + 0.35 + c * ((LW - 0.7) / 12) + (R() - 0.5) * 0.12, 0, 0.25 + r * 0.22 + (R() - 0.5) * 0.08];
    } else {
      const e = edge(1.3, 0.7);
      p = [e[0], 0, LD - 0.5 + e[1]];
    }
    return rx(tr(p, 0, -1.0, -LD / 2 + 0.4), 0.38);
  });
  const desktop = make(() => {
    const q = R();
    let p: V3;
    if (q < 0.2) {
      const e = edge(3.3, 2.1);
      p = [e[0] - 0.9, e[1] + 0.55, 0];
    } else if (q < 0.44) {
      const l = lines(3.3, 2.1, 8);
      p = [l[0] - 0.9, l[1] + 0.55, 0];
    } else if (q < 0.5) p = [-0.9 + (R() - 0.5) * 0.25, -0.5 - R() * 0.55, (R() - 0.5) * 0.15];
    else if (q < 0.56) {
      const a = R() * P * 2;
      const r = Math.sqrt(R());
      p = [-0.9 + Math.cos(a) * 0.9 * r, -1.07, Math.sin(a) * 0.35 * r];
    } else if (q < 0.76) p = tr(boxEdge(1, 2.6, 1.9), 1.95, 0, -0.2);
    else if (q < 0.9) {
      const r = (R() * 9) | 0;
      p = [1.63 + R() * 0.64, 0.9 - r * 0.14, 0.75];
    } else if (q < 0.94) {
      const a = R() * P * 2;
      p = [1.95 + Math.cos(a) * 0.12, -0.75 + Math.sin(a) * 0.12, 0.75];
    } else p = [1.95 + (R() - 0.5), (R() - 0.5) * 2.6, 0.75];
    return ry(p, -0.3);
  });
  /** One handset; variant 0 has a pill notch, variant 1 a punch-hole camera. */
  const phoneLocal = (variant: number): V3 => {
    const pw = 1.6;
    const ph = 3.2;
    const k = R();
    if (k < 0.4) {
      const c = 0.22;
      let [x, y] = edge(pw, ph);
      if (Math.abs(x) > pw / 2 - c && Math.abs(y) > ph / 2 - c) {
        const sx = Math.sign(x);
        const sy = Math.sign(y);
        const a = Math.atan2(y - sy * (ph / 2 - c), x - sx * (pw / 2 - c));
        x = sx * (pw / 2 - c) + Math.cos(a) * c;
        y = sy * (ph / 2 - c) + Math.sin(a) * c;
      }
      return [x, y, (R() - 0.5) * 0.2];
    }
    if (k < 0.82) {
      const c = (R() * 3) | 0;
      const r = (R() * 4) | 0;
      const e = edge(0.32, 0.32);
      return [-0.45 + c * 0.45 + e[0], 0.95 - r * 0.5 + e[1], 0];
    }
    if (k < 0.88) {
      if (variant === 0) return [(R() - 0.5) * 0.45, 1.38 + (R() - 0.5) * 0.08, 0];
      const a = R() * P * 2;
      return [Math.cos(a) * 0.07, 1.4 + Math.sin(a) * 0.07, 0];
    }
    return [(R() - 0.5) * 0.6, -1.42, 0];
  };
  const phones = make(() =>
    R() < 0.5
      ? tr(ry(phoneLocal(0), 0.35), -1.15, 0.15, 0)
      : tr(ry(phoneLocal(1), -0.35), 1.15, -0.15, 0),
  );
  // A small neural net: four layers of nodes, fully connected layer to layer.
  const layerSizes = [4, 6, 6, 3];
  const layerX = [-2.5, -0.85, 0.85, 2.5];
  const nodes: V3[][] = layerSizes.map((n, l) =>
    Array.from({ length: n }, (_, j): V3 => [layerX[l], (j - (n - 1) / 2) * 0.62, j % 2 ? 0.25 : -0.25]),
  );
  const allNodes = nodes.flat();
  const links: [V3, V3][] = [];
  for (let l = 0; l < 3; l++) nodes[l].forEach((a) => nodes[l + 1].forEach((b) => links.push([a, b])));
  const ai = make(() => {
    if (R() < 0.38) {
      const n = allNodes[(R() * allNodes.length) | 0];
      const u = R() * P * 2;
      const v = Math.acos(2 * R() - 1);
      const r = 0.16 * Math.cbrt(R()) + 0.04;
      return [n[0] + r * Math.sin(v) * Math.cos(u), n[1] + r * Math.sin(v) * Math.sin(u), n[2] + r * Math.cos(v)];
    }
    const e = links[(R() * links.length) | 0];
    return seg(e[0], e[1]);
  });
  /** Which of the three stacked SaaS layers a point belongs to, for the bob. */
  const saasLayer = new Int8Array(N);
  const saas = make((i) => {
    const L = i % 3;
    saasLayer[i] = L;
    let x: number;
    let z: number;
    if (R() < 0.5) [x, z] = edge(3.4, 2.3);
    else {
      const c = (R() * 3) | 0;
      const r = (R() * 2) | 0;
      const e = edge(0.85, 0.7);
      x = -1.1 + c * 1.1 + e[0];
      z = -0.55 + r * 1.1 + e[1];
    }
    return rx([x, (L - 1) * 1.0, z], 0.55);
  });
  const puzzle = sample(200, 200, (cx) => {
    cx.fillRect(40, 60, 100, 100);
    cx.beginPath();
    cx.arc(90, 60, 22, 0, P * 2);
    cx.arc(140, 110, 22, 0, P * 2);
    cx.fill();
  });
  const extension = make(() => {
    const q = R();
    const W = 4.4;
    const H = 2.9;
    let p: V3;
    if (q < 0.2) {
      const e = edge(W, H);
      p = [e[0], e[1], 0];
    } else if (q < 0.28) p = [-W / 2 + R() * W, H / 2 - 0.5, 0];
    else if (q < 0.33) {
      const d = (R() * 3) | 0;
      const a = R() * P * 2;
      p = [-W / 2 + 0.3 + d * 0.25 + Math.cos(a) * 0.07, H / 2 - 0.25 + Math.sin(a) * 0.07, 0];
    } else if (q < 0.4) {
      const e = edge(2.2, 0.26);
      p = [e[0] - 0.2, e[1] + H / 2 - 0.25, 0];
    } else if (q < 0.62) {
      const l = lines(W - 0.4, H - 0.9, 7);
      p = [l[0], l[1] - 0.35, 0];
    } else {
      const s = puzzle[(R() * puzzle.length) | 0];
      p = [1.6 + ((s[0] - 100) / 200) * 1.4, 1.0 - ((s[1] - 100) / 200) * 1.4, 0.5 + (R() - 0.5) * 0.25];
    }
    return ry(p, -0.25);
  });
  const framework = make(() => {
    const ci = (R() * 27) | 0;
    const cx = (ci % 3) - 1;
    const cy = (((ci / 3) | 0) % 3) - 1;
    const cz = ((ci / 9) | 0) - 1;
    const s = 0.95;
    const p: V3 =
      R() < 0.75
        ? boxEdge(0.68, 0.68, 0.68)
        : [(R() - 0.5) * 0.68, (R() - 0.5) * 0.68, 0.34 * (R() < 0.5 ? 1 : -1)];
    return rx(ry([p[0] + cx * s, p[1] + cy * s, p[2] + cz * s], 0.6), 0.45);
  });

  return {
    glyph,
    knot,
    work,
    globe,
    mark,
    orbit,
    services: [laptop, desktop, phones, ai, saas, extension, framework],
    saasLayer,
  };
}

export function createParticleField({ canvas, root, marquee, service }: Options): ParticleField {
  const N = COUNT;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
  camera.position.z = 9;
  const group = new THREE.Group();
  scene.add(group);

  const material = createMaterial(renderer.getPixelRatio());

  // The stars drift slowly with scroll.
  const starGeo = createStars(STARS);
  const stars = new THREE.Points(starGeo, material);
  scene.add(stars);

  const shapes = buildShapes(N);
  let svc = service;
  /** The services slot is live: it tweens between service shapes on demand. */
  const live1 = new Float32Array(shapes.services[svc]);
  /** So is the mark's: part of it orbits. */
  const live5 = new Float32Array(shapes.mark);
  const sequence = [shapes.glyph, live1, shapes.knot, shapes.work, shapes.globe, live5];
  let svcFrom: Float32Array | null = null;
  let svcT0 = 0;

  const scatter = new Float32Array(N * 3);
  const phase = new Float32Array(N);
  const size = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const u = Math.random() * Math.PI * 2;
    const v = Math.acos(2 * Math.random() - 1);
    const r = 2.5 + Math.random() * 5;
    scatter[i * 3] = r * Math.sin(v) * Math.cos(u) * 1.4;
    scatter[i * 3 + 1] = r * Math.sin(v) * Math.sin(u);
    scatter[i * 3 + 2] = r * Math.cos(v);
    phase[i] = Math.random() * 6.28;
    size[i] = Math.random() < 0.04 ? 2.6 : 0.7 + Math.random() * 1.1;
  }
  const geometry = new THREE.BufferGeometry();
  const position = new THREE.BufferAttribute(new Float32Array(shapes.glyph), 3);
  const color = new THREE.BufferAttribute(new Float32Array(N * 3), 3);
  geometry.setAttribute('position', position);
  geometry.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
  geometry.setAttribute('aColor', color);
  group.add(new THREE.Points(geometry, material));

  /*
   * ThemeProvider owns the `dark` class on <html>; read it rather than keep a
   * second copy of the mode. Checked every frame, so a toggle is picked up
   * without any wiring between the header and this module.
   */
  const isLight = () => !document.documentElement.classList.contains('dark');
  let light = isLight();

  const applyTheme = () => {
    // Additive blending glows on a dark ground and vanishes on a light one.
    material.blending = light ? THREE.NormalBlending : THREE.AdditiveBlending;
    material.uniforms.uLight.value = light ? 1 : 0;
    material.needsUpdate = true;
    const pal = (light ? PALETTE.light : PALETTE.dark).map((c) => new THREE.Color(c));
    for (let i = 0; i < N; i++) {
      const r = Math.random();
      const c = r < 0.45 ? pal[0] : r < 0.85 ? pal[1] : pal[2];
      const b = light ? 0.8 + Math.random() * 0.2 : 0.45 + Math.random() * 0.4;
      color.array[i * 3] = c.r * b;
      color.array[i * 3 + 1] = c.g * b;
      color.array[i * 3 + 2] = c.b * b;
    }
    color.needsUpdate = true;
  };
  applyTheme();

  const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
  const onMove = (e: PointerEvent) => {
    mouse.tx = e.clientX / window.innerWidth - 0.5;
    mouse.ty = e.clientY / window.innerHeight - 0.5;
  };
  const onResize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('resize', onResize);
  onResize();

  const sections = Array.from(root.querySelectorAll<HTMLElement>('[data-shape]'));
  /** Fractional section index: 2.4 is 40% of the way from section 2 to 3. */
  const scrollProgress = () => {
    const mid = window.innerHeight / 2;
    const centres = sections.map((el) => {
      const b = el.getBoundingClientRect();
      return b.top + b.height / 2;
    });
    if (!centres.length || mid <= centres[0]) return 0;
    for (let k = 0; k < centres.length - 1; k++)
      if (mid < centres[k + 1]) return k + (mid - centres[k]) / (centres[k + 1] - centres[k]);
    return centres.length - 1;
  };

  let f = 0;
  let raf = 0;
  const t0 = performance.now();
  const n3 = N * 3;
  const last = sequence.length - 1;

  const frame = () => {
    const now = performance.now();
    const time = (now - t0) / 1000;
    const wide = window.innerWidth >= 900;

    if (isLight() !== light) {
      light = !light;
      applyTheme();
    }

    f += (scrollProgress() - f) * 0.08;
    const a = Math.max(0, Math.min(last, Math.floor(f)));
    const b = Math.min(last, a + 1);
    const t = f - a;
    const m = a === b ? 0 : smooth((t - 0.2) / 0.65);
    const burst = Math.sin(m * Math.PI) * 0.95;

    if (a === SERVICES || b === SERVICES) {
      const e = smooth(Math.min(1, (now - svcT0) / 1100));
      const to = shapes.services[svc];
      const from = svcFrom ?? to;
      const pop = Math.sin(e * Math.PI) * 0.45;
      const bob = svc === 4;
      for (let i = 0; i < n3; i += 3) {
        live1[i] = from[i] + (to[i] - from[i]) * e + scatter[i] * pop;
        live1[i + 1] =
          from[i + 1] + (to[i + 1] - from[i + 1]) * e + scatter[i + 1] * pop +
          (bob ? Math.sin(time * 1.6 + shapes.saasLayer[i / 3] * 1.3) * 0.3 * e : 0);
        live1[i + 2] = from[i + 2] + (to[i + 2] - from[i + 2]) * e + scatter[i + 2] * pop;
      }
    }
    if (a === MARK || b === MARK) {
      const mk = shapes.mark;
      const ang = time * 0.5;
      const ca = Math.cos(ang);
      const sa = Math.sin(ang);
      const tilt = -0.35;
      for (let i = 0; i < n3; i += 3) {
        if (shapes.orbit[i / 3]) {
          const x = mk[i];
          const z0 = mk[i + 1] / 0.32;
          const rx0 = x * ca - z0 * sa;
          const rz0 = x * sa + z0 * ca;
          live5[i] = rx0;
          live5[i + 1] = rz0 * Math.sin(tilt) * 0.55 + mk[i + 2];
          live5[i + 2] = rz0 * Math.cos(tilt);
        } else {
          live5[i] = mk[i];
          live5[i + 1] = mk[i + 1];
          live5[i + 2] = mk[i + 2];
        }
      }
    }

    const A = sequence[a];
    const B = sequence[b];
    const pos = position.array;
    for (let i = 0; i < n3; i += 3) {
      const w = Math.sin(time * 0.8 + phase[i / 3]) * 0.025;
      pos[i] = A[i] + (B[i] - A[i]) * m + scatter[i] * burst + w;
      pos[i + 1] = A[i + 1] + (B[i + 1] - A[i + 1]) * m + scatter[i + 1] * burst + w;
      pos[i + 2] = A[i + 2] + (B[i + 2] - A[i + 2]) * m + scatter[i + 2] * burst;
    }
    position.needsUpdate = true;

    // Sit the shape in the half of the 1440px column the copy is not using.
    const visH = 2 * camera.position.z * Math.tan((25 * Math.PI) / 180);
    const visW = (visH * window.innerWidth) / window.innerHeight;
    const column = Math.min(window.innerWidth, 1440);
    const halfW = (column / 2 / window.innerWidth) * visW;
    const offset = wide ? (column / 4 / window.innerWidth) * visW : 0;
    group.position.x = (SIDE[a] + (SIDE[b] - SIDE[a]) * m) * offset;
    group.scale.setScalar(wide ? Math.max(0.7, Math.min(1.3, halfW / 6)) : 0.72);
    // On a narrow screen the shape sits behind the copy, so it is dimmed.
    material.uniforms.uOp.value = (wide ? 1 : 0.55) * (light ? 0.85 : 1);

    mouse.x += (mouse.tx - mouse.x) * 0.05;
    mouse.y += (mouse.ty - mouse.y) * 0.05;
    const calm = a === MARK ? 1 : b === MARK ? m : 0;
    const spin = a === 4 ? 1 - m : b === 4 ? m : 0;
    group.rotation.y = Math.sin(time * 0.25) * 0.4 * (1 - calm * 0.6) + mouse.x * 0.5 + time * 0.15 * spin;
    group.rotation.x = 0.08 + mouse.y * 0.25;
    stars.rotation.y = time * 0.006;
    stars.position.y = f * 2.2;
    if (marquee) marquee.style.transform = `translateX(${-(f - 0.3) * 38}%)`;

    renderer.render(scene, camera);
    raf = requestAnimationFrame(frame);
  };
  frame();

  return {
    setService(index) {
      if (index === svc) return;
      svc = index;
      svcFrom = new Float32Array(live1);
      svcT0 = performance.now();
    },
    dispose() {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('resize', onResize);
      geometry.dispose();
      starGeo.dispose();
      material.dispose();
      renderer.dispose();
    },
  };
}
